import { z } from 'zod';

/**
 * Task #189 — Standardized social-vulnerability index (derived, not stored).
 *
 * A community already carries `population` (approximate census total) and
 * `vulnerable_population` (a transparent estimate of elderly + medically
 * dependent + isolated residents, §4.25). This module derives a standardized
 * index from those two fields at READ TIME — there is no stored column, no
 * migration, and no census ingest in this slice.
 *
 * THE FORMULA (documented, deliberate):
 *
 *   ratio = vulnerable_population ÷ population        (clamped to [0, 1])
 *
 *   band  = unknown     population missing/zero OR vulnerable count missing —
 *                       the index is honestly "unknown", NEVER fabricated
 *           low         ratio < 15%
 *           moderate    15% ≤ ratio < 22.5%
 *           high        22.5% ≤ ratio < 30%
 *           very_high   ratio ≥ 30%
 *
 * Threshold rationale: the vulnerable-population definition (elderly + medically
 * dependent + isolated) approximates the national senior share (~19% of
 * Canadians were 65+ in the 2021 census). `moderate` brackets that national
 * share; `low` is clearly below it; `high` is clearly above; `very_high` is
 * ≥1.5× the share. On the seeded Ontario data this separates the urban centres
 * (Barrie 18% → moderate) from the remote fly-in First Nations (Neskantaga
 * 28% → high), with `very_high` reserved for genuinely extreme real figures.
 *
 * A vulnerable count exceeding the population is treated as a data-entry
 * inconsistency: the ratio clamps to 1 (→ very_high) rather than erroring,
 * since an overstated vulnerable count still signals high vulnerability.
 *
 * SUPERSEDED FOR THE STANDARDIZED INDEX (task #234, migration 0068): the
 * census-anchored BANDS above are RETIRED from product surfaces — the headline
 * chip, briefing line, and map shading now key on the StatCan CIMD 2021
 * Situational Vulnerability quintile (`cimd.ts`), the government's own bands.
 * The RATIO survives as the operator-entered live-counts complement
 * ("operator-entered vulnerable share"), rendered without a band so the two
 * can't be confused: CIMD = standardized census reference, this ratio = what
 * operators have entered live for headcount/wellness work.
 */

export const SOCIAL_VULNERABILITY_BANDS = ['low', 'moderate', 'high', 'very_high'] as const;
export const SocialVulnerabilityBandSchema = z.enum(SOCIAL_VULNERABILITY_BANDS);
export type SocialVulnerabilityBand = z.infer<typeof SocialVulnerabilityBandSchema>;

/** Band thresholds (lower bound of each band, as a ratio). */
export const SOCIAL_VULNERABILITY_THRESHOLDS = {
  moderate: 0.15,
  high: 0.225,
  very_high: 0.3,
} as const;

/** Short human label, paired with any colour render (colour never load-bearing). */
export const SOCIAL_VULNERABILITY_BAND_LABEL: Record<SocialVulnerabilityBand | 'unknown', string> =
  {
    unknown: 'Unknown',
    low: 'Low',
    moderate: 'Moderate',
    high: 'High',
    very_high: 'Very high',
  };

export interface SocialVulnerability {
  /** The derived band, or 'unknown' when either input is missing (honest gap). */
  band: SocialVulnerabilityBand | 'unknown';
  /** vulnerable ÷ population clamped to [0,1]; null when the band is unknown. */
  ratio: number | null;
}

/**
 * Derive the social-vulnerability index for one community. Tolerant of missing
 * data: any gap (null/undefined population or vulnerable count, zero or negative
 * population, negative vulnerable count) yields `{ band: 'unknown', ratio: null }`
 * — a score is never fabricated.
 */
export function deriveSocialVulnerability(
  population: number | null | undefined,
  vulnerablePopulation: number | null | undefined,
): SocialVulnerability {
  if (
    population === null ||
    population === undefined ||
    !Number.isFinite(population) ||
    population <= 0 ||
    vulnerablePopulation === null ||
    vulnerablePopulation === undefined ||
    !Number.isFinite(vulnerablePopulation) ||
    vulnerablePopulation < 0
  ) {
    return { band: 'unknown', ratio: null };
  }
  const ratio = Math.min(1, vulnerablePopulation / population);
  const t = SOCIAL_VULNERABILITY_THRESHOLDS;
  const band: SocialVulnerabilityBand =
    ratio >= t.very_high ? 'very_high' : ratio >= t.high ? 'high' : ratio >= t.moderate ? 'moderate' : 'low';
  return { band, ratio };
}

/** "26%" display form of a derived ratio (null in → null out). */
export function formatVulnerabilityPercent(ratio: number | null): string | null {
  if (ratio === null || !Number.isFinite(ratio)) return null;
  return `${Math.round(ratio * 100)}%`;
}
